import { CredentialSchema } from '../types/W3CCredentialTypes';
import config from '../config/environment';

export interface ValidationResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ValidationError[];
  previewData: CredentialRecord[];
  allRecords: CredentialRecord[];  // Add all records
}

export interface ValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
  severity: 'error' | 'warning';
}

export interface CredentialRecord {
  id: string;
  data: Record<string, string>;
  isValid: boolean;
  errors: string[];
}

export async function validateCsvData(file: File, schema: CredentialSchema): Promise<ValidationResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());

      // Verify required fields
      const requiredFields = schema.fields.filter(f => f.required).map(f => f.name);
      const missingRequiredFields = requiredFields.filter(field => !headers.includes(field));

      if (missingRequiredFields.length > 0) {
        reject(new Error(`Missing required fields in CSV: ${missingRequiredFields.join(', ')}`));
        return;
      }

      // Verify holder_email field exists
      if (!headers.includes('holder_email')) {
        reject(new Error('CSV must include a holder_email column'));
        return;
      }

      const records: CredentialRecord[] = [];
      const errors: ValidationError[] = [];
      let validRows = 0;
      let invalidRows = 0;

      // Process each row (skip header)
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const record: CredentialRecord = {
          id: `row-${i}`,
          data: {},
          isValid: true,
          errors: []
        };

        // Map values to fields
        let rowHasError = false;
        headers.forEach((header, index) => {
          const value = values[index] || '';
          record.data[header] = value;

          // Find corresponding schema field
          const schemaField = schema.fields.find(f => f.name === header);
          if (schemaField) {
            // Validate required fields
            if (schemaField.required && !value) {
              rowHasError = true;
              record.errors.push(`${header} is required`);
              errors.push({
                row: i,
                field: header,
                value: value,
                error: 'Required field is empty',
                severity: 'error'
              });
            }

            // Validate field types
            if (value) {
              switch (schemaField.type) {
                case 'email':
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    rowHasError = true;
                    record.errors.push(`Invalid email format for ${header}`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Invalid email format',
                      severity: 'error'
                    });
                  }
                  break;
                case 'number':
                  if (isNaN(Number(value))) {
                    rowHasError = true;
                    record.errors.push(`${header} must be a number`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Must be a number',
                      severity: 'error'
                    });
                  }
                  break;
                case 'decimal':
                  if (isNaN(Number(value))) {
                    rowHasError = true;
                    record.errors.push(`${header} must be a decimal number`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Must be a decimal number',
                      severity: 'error'
                    });
                  }
                  break;
                case 'boolean':
                case 'bool':
                  const lowerValue = value.toLowerCase();
                  if (lowerValue !== 'true' && lowerValue !== 'false') {
                    rowHasError = true;
                    record.errors.push(`${header} must be true or false`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Must be true or false',
                      severity: 'error'
                    });
                  }
                  break;
                case 'date':
                  if (isNaN(Date.parse(value))) {
                    rowHasError = true;
                    record.errors.push(`Invalid date format for ${header}`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Invalid date format',
                      severity: 'error'
                    });
                  }
                  break;
                case 'url':
                  try {
                    new URL(value);
                  } catch {
                    rowHasError = true;
                    record.errors.push(`Invalid URL format for ${header}`);
                    errors.push({
                      row: i,
                      field: header,
                      value: value,
                      error: 'Invalid URL format',
                      severity: 'error'
                    });
                  }
                  break;
              }
            }
          }
        });

        record.isValid = !rowHasError;
        if (rowHasError) {
          invalidRows++;
        } else {
          validRows++;
        }
        records.push(record);
      }

      resolve({
        totalRows: lines.length - 1, // Subtract header row
        validRows,
        invalidRows,
        errors,
        previewData: records.slice(0, 5), // Show first 5 records as preview
        allRecords: records // Include all records
      });
    };

    reader.onerror = () => {
      reject(new Error('Failed to read CSV file'));
    };

    reader.readAsText(file);
  });
}

export async function issueCredentialsBulk(
  records: CredentialRecord[],
  schema: CredentialSchema,
  onProgress: (progress: number) => void
): Promise<{ success: boolean; errors?: string[] }> {
  const batchSize = 5; // Process 5 records in parallel
  const errors: string[] = [];
  let processed = 0;
  const API_BASE_URL = config.REACT_APP_API_BASE_URL;

  // Process records in batches
  for (let i = 0; i < records.length; i += batchSize) {
    const batch = records.slice(i, i + batchSize);
    const promises = batch.map(record => {
      // Transform credential fields to ensure proper types
      const transformedFields: Record<string, string | boolean | number> = {};
      schema.fields.forEach(field => {
        const value = record.data[field.name];
        if (value !== undefined && value !== null && value !== '') {
          // Convert number and decimal types to actual numbers
          if (field.type === 'number' || field.type === 'decimal') {
            transformedFields[field.name] = parseFloat(value);
          } else if (field.type === 'boolean' || field.type === 'bool') {
            // Convert boolean strings to actual booleans
            transformedFields[field.name] = value.toLowerCase() === 'true';
          } else {
            transformedFields[field.name] = value;
          }
        }
      });

      return fetch(`${API_BASE_URL}/api/credentials/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
        },
        body: JSON.stringify({
          credential_schema_id: schema.id,
          holder_email: record.data.holder_email,
          credential_fields: transformedFields
        })
      }).then(async response => {
        if (!response.ok) {
          const error = await response.text();
          throw new Error(`Failed to issue credential for ${record.data.holder_email}: ${error}`);
        }
        return response.json();
      }).catch(error => {
        errors.push(error.message);
        return null;
      })
    });

    // Wait for batch to complete
    await Promise.all(promises);

    processed += batch.length;
    onProgress((processed / records.length) * 100);
  }

  return {
    success: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined
  };
}